import React, { useState, useEffect } from 'react';
import { useAuth, UserRole } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Shield, BookOpen, Building2, Wrench, Flame, Siren,
    AlertTriangle, Construction, Bus, LifeBuoy, ListChecks
} from 'lucide-react';
import CommuneHeader from '../components/commune/CommuneHeader';
import CommuneOverviewTab from '../components/commune/CommuneOverviewTab';
import CommuneJournalTab from '../components/commune/CommuneJournalTab';
import CommuneCareTab from '../components/commune/CommuneCareTab';
import CommuneActionsTab from '../components/commune/CommuneActionsTab';
import CommuneSecoursTab from '../components/commune/CommuneSecoursTab';
import CommuneZonesTab from '../components/commune/CommuneZonesTab';
import CommuneRestrictionsTab from '../components/commune/CommuneRestrictionsTab';
import CommuneTransportsTab from '../components/commune/CommuneTransportsTab';
import CommuneRequisitionsTab from '../components/commune/CommuneRequisitionsTab';
import CommuneRetourTab from '../components/commune/CommuneRetourTab';
import CommuneDialog from '../components/commune/CommuneDialog';
import CommuneSituationMap from '../components/commune/CommuneSituationMap';
import useLiveRefresh from '../hooks/useLiveRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { id: 'dashboard', label: 'Vue generale', icon: Shield },
    { id: 'journal', label: 'Journal', icon: BookOpen },
    { id: 'care', label: 'Centres CARE', icon: Building2 },
    { id: 'actions', label: "Demandes d'action", icon: Wrench },
    { id: 'pompiers', label: 'Pompiers', icon: Flame },
    { id: 'police', label: 'Police', icon: Siren },
    { id: 'zones', label: 'Zones', icon: AlertTriangle },
    { id: 'restrictions', label: 'Restrictions', icon: Construction },
    { id: 'transports', label: 'Transports', icon: Bus },
    { id: 'evacuation', label: 'Requisitions', icon: LifeBuoy },
    { id: 'retour', label: 'Retour normalité', icon: ListChecks },
];

const CommuneViewPage = () => {
    const { token, user } = useAuth();
    const [activeTab, setActiveTab] = useState(() => {
        // Deep-link: lire ?tab=X dans l'URL au montage
        const params = new URLSearchParams(window.location.search);
        const t = params.get('tab');
        return t && TABS.some(tab => tab.id === t) ? t : 'dashboard';
    });
    const [loading, setLoading] = useState(true);
    const [dashData, setDashData] = useState(null);
    const [journal, setJournal] = useState([]);
    const [interventions, setInterventions] = useState([]);
    const [intTypes, setIntTypes] = useState([]);
    const [zones, setZones] = useState([]);
    const [restrictions, setRestrictions] = useState([]);
    const [restrictionTypes, setRestrictionTypes] = useState([]);
    const [transports, setTransports] = useState([]);
    const [evacPoints, setEvacPoints] = useState([]);
    const [pompiers, setPompiers] = useState([]);
    const [pompiersTypes, setPompiersTypes] = useState([]);
    const [policeActions, setPoliceActions] = useState([]);
    const [policeTypes, setPoliceTypes] = useState([]);
    const [careCentersFull, setCareCentersFull] = useState([]);
    const [requisitionTypes, setRequisitionTypes] = useState([]);
    const [retourData, setRetourData] = useState({ items: [], total: 0, completed: 0, progress: 0 });
    const [missionsSummary, setMissionsSummary] = useState({ active_missions: 0, closed_missions: 0, total_sub_missions: 0, closed_sub_missions: 0, missions: [] });
    const [incidents, setIncidents] = useState([]);
    const [drawings, setDrawings] = useState([]);
    // Dialogs
    const [dlg, setDlg] = useState(null); // { type: 'journal'|'action'|'zone'|'restriction'|'transport'|'evac'|'pompiers'|'police', data: {}, editing: bool }
    const [fsMapOpen, setFsMapOpen] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };
    const canEditJournal = user?.role === UserRole.SECRETARIAT_COMMUNICATION || user?.role === UserRole.CHEF_DISPOSITIF;
    const canEditServices = user?.role === UserRole.SOUTIEN_LOG_COMMUNE || user?.role === UserRole.CHEF_DISPOSITIF;
    const canEditZones = canEditServices || user?.role === UserRole.POLICE_MUNICIPALE;

    const fetchAll = async () => {
        try {
            const [dashRes, jRes, iRes, tRes, zRes, rRes, rtRes, trRes, eRes, pRes, ptRes, poRes, potRes, ccRes, reqTypesRes, rnRes, msRes, incRes, drawRes] = await Promise.all([
                axios.get(`${API}/dashboard/commune`, { headers }),
                axios.get(`${API}/journal-bord`, { headers }),
                axios.get(`${API}/services-techniques/interventions`, { headers }),
                axios.get(`${API}/services-techniques/types`, { headers }),
                axios.get(`${API}/services-techniques/zones-dangereuses`, { headers }),
                axios.get(`${API}/services-techniques/restrictions-routieres`, { headers }),
                axios.get(`${API}/services-techniques/restriction-types`, { headers }),
                axios.get(`${API}/services-techniques/transports`, { headers }),
                axios.get(`${API}/services-techniques/points-evacuation`, { headers }),
                axios.get(`${API}/services-techniques/pompiers`, { headers }),
                axios.get(`${API}/services-techniques/pompiers/types`, { headers }),
                axios.get(`${API}/services-techniques/police`, { headers }),
                axios.get(`${API}/services-techniques/police/types`, { headers }),
                axios.get(`${API}/care-centers`, { headers }),
                axios.get(`${API}/services-techniques/points-evacuation/types`, { headers }),
                axios.get(`${API}/retour-normalite/checklist`, { headers }),
                axios.get(`${API}/retour-normalite/missions-summary`, { headers }),
                axios.get(`${API}/incidents?active_only=true`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/map/drawings`, { headers }).catch(() => ({ data: [] })),
            ]);
            setDashData(dashRes.data); setJournal(jRes.data); setInterventions(iRes.data);
            setIntTypes(tRes.data); setZones(zRes.data); setRestrictions(rRes.data);
            setRestrictionTypes(rtRes.data); setTransports(trRes.data); setEvacPoints(eRes.data);
            setPompiers(pRes.data); setPompiersTypes(ptRes.data);
            setPoliceActions(poRes.data); setPoliceTypes(potRes.data);
            setCareCentersFull(ccRes.data); setRequisitionTypes(reqTypesRes.data);
            setRetourData(rnRes.data); setMissionsSummary(msRes.data);
            setIncidents(incRes.data); setDrawings(drawRes.data);
        } catch { toast.error('Erreur chargement'); }
        finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 60000); return () => clearInterval(i); }, []);

    useLiveRefresh(fetchAll);

    const openDlg = (type, data = {}, editing = false) => setDlg({ type, data, editing });
    const closeDlg = () => setDlg(null);

    const saveDlg = async () => {
        if (!dlg) return;
        const { type, data, editing } = dlg;
        try {
            const endpoints = {
                journal: '/journal-bord',
                action: '/services-techniques/interventions',
                zone: '/services-techniques/zones-dangereuses',
                restriction: '/services-techniques/restrictions-routieres',
                transport: '/services-techniques/transports',
                evac: '/services-techniques/points-evacuation',
                pompiers: '/services-techniques/pompiers',
                police: '/services-techniques/police',
            };
            const url = `${API}${endpoints[type]}`;
            if (editing && data.id) {
                await axios.put(`${url}/${data.id}`, data, { headers });
                toast.success('Mis a jour');
            } else {
                await axios.post(url, data, { headers });
                toast.success('Cree');
            }
            closeDlg();
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const updateStatus = async (endpoint, id, statut) => {
        try { await axios.put(`${API}${endpoint}/${id}`, { statut }, { headers }); fetchAll(); }
        catch { toast.error('Erreur'); }
    };
    const deleteItem = async (endpoint, id) => {
        try { await axios.delete(`${API}${endpoint}/${id}`, { headers }); fetchAll(); }
        catch { toast.error('Erreur'); }
    };
    const generateArrete = async (reqId) => {
        try {
            const r = await axios.post(`${API}/services-techniques/points-evacuation/${reqId}/generate-arrete`, {}, { headers });
            toast.success(`Arrete ${r.data.number} cree (brouillon)`);
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur generation arrete'); }
    };

    if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;

    const careCenters = dashData?.care_centers || [];
    const mapCenter = careCenters.length > 0 && careCenters[0].latitude ? [careCenters[0].latitude, careCenters[0].longitude] : [43.53, 5.45];

    return (
        <div className="space-y-5" data-testid="commune-view-page">
            <CommuneHeader token={token} dashData={dashData} />

            {/* Tabs - wrap to multiple lines on narrower screens */}
            <div className="flex flex-wrap gap-1 p-1 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                {TABS.map(tab => {
                    const Icon = tab.icon;
                    const badge = tab.id === 'zones' ? zones.filter(z => z.statut === 'signale').length
                        : tab.id === 'restrictions' ? restrictions.filter(r => r.statut === 'active').length : 0;
                    return (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-700/50'}`}>
                            <Icon className="w-3.5 h-3.5 flex-shrink-0" /> {tab.label}
                            {badge > 0 && <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center flex-shrink-0">{badge}</span>}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'dashboard' && dashData && (
                <CommuneOverviewTab
                    dashData={dashData} careCenters={careCenters} mapCenter={mapCenter}
                    interventions={interventions} zones={zones} restrictions={restrictions}
                    transports={transports} evacPoints={evacPoints} incidents={incidents} drawings={drawings}
                    onSelectTab={setActiveTab} onFullscreen={() => setFsMapOpen(true)}
                />
            )}
            {activeTab === 'journal' && (
                <CommuneJournalTab journal={journal} token={token} canEditJournal={canEditJournal} openDlg={openDlg} deleteItem={deleteItem} />
            )}
            {activeTab === 'care' && <CommuneCareTab careCentersFull={careCentersFull} />}
            {activeTab === 'actions' && (
                <CommuneActionsTab interventions={interventions} intTypes={intTypes} canEditServices={canEditServices} openDlg={openDlg} updateStatus={updateStatus} deleteItem={deleteItem} />
            )}
            {activeTab === 'pompiers' && (
                <CommuneSecoursTab kind="pompiers" items={pompiers} types={pompiersTypes} canEditServices={canEditServices} openDlg={openDlg} updateStatus={updateStatus} deleteItem={deleteItem} />
            )}
            {activeTab === 'police' && (
                <CommuneSecoursTab kind="police" items={policeActions} types={policeTypes} canEditServices={canEditServices} openDlg={openDlg} updateStatus={updateStatus} deleteItem={deleteItem} />
            )}
            {activeTab === 'zones' && (
                <CommuneZonesTab zones={zones} canEditZones={canEditZones} openDlg={openDlg} updateStatus={updateStatus} />
            )}
            {activeTab === 'restrictions' && (
                <CommuneRestrictionsTab restrictions={restrictions} restrictionTypes={restrictionTypes} canEditZones={canEditZones} openDlg={openDlg} updateStatus={updateStatus} />
            )}
            {activeTab === 'transports' && (
                <CommuneTransportsTab transports={transports} canEditServices={canEditServices} openDlg={openDlg} updateStatus={updateStatus} />
            )}
            {activeTab === 'evacuation' && (
                <CommuneRequisitionsTab evacPoints={evacPoints} requisitionTypes={requisitionTypes} canEditServices={canEditServices} openDlg={openDlg} updateStatus={updateStatus} deleteItem={deleteItem} generateArrete={generateArrete} />
            )}
            {activeTab === 'retour' && (
                <CommuneRetourTab retourData={retourData} missionsSummary={missionsSummary} token={token} onRefresh={fetchAll} />
            )}

            <CommuneDialog
                dlg={dlg} setDlg={setDlg} onClose={closeDlg} onSave={saveDlg}
                intTypes={intTypes} restrictionTypes={restrictionTypes} requisitionTypes={requisitionTypes}
                pompiersTypes={pompiersTypes} policeTypes={policeTypes}
            />

            <CommuneSituationMap
                open={fsMapOpen} onClose={() => setFsMapOpen(false)} mapCenter={mapCenter}
                zones={zones} restrictions={restrictions} careCenters={careCenters}
                transports={transports} evacPoints={evacPoints} incidents={incidents} drawings={drawings}
            />
        </div>
    );
};

export default CommuneViewPage;
